import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import EmptyTypes from 'common/enums/emptyTypes';
import { useSettingValue } from 'common/hooks/useSettingValue';
import {
    fetchOrganizationProviderAction,
    fetchProductsSearchEngineAction,
    getOrganization,
    getProductsSearchEngine,
    getProvider,
} from 'redux/slices/organizationSlice';
import {
    fetchProductsDiscountsAction,
    getPendingProductDiscounts,
    getProductDiscounts,
} from 'redux/slices/productDiscountSlice';
import {
    fetchProductsAction,
    fetchProductsV2Action,
    getProducts,
    getProductsError,
    getProductsPending,
    hasMoreProducts,
} from 'redux/slices/productSlice';
import { getProductDiscount } from '../../../common/utils/productDiscount';
import EventFormSelectAssets from './EventFormSelectAssets';
import EventFormSelectAssetsFilter from './EventFormSelectAssetsFilters';

const EventFormSelectProducts = (props) => {
    const { selectedProducts, close, onConfirm, type, searchMinLength, emptyDescription } = props;

    const { t } = useTranslation();
    const error = useSelector(getProductsError);
    const products = useSelector(getProducts);
    const pending = useSelector(getProductsPending);
    const hasMore = useSelector(hasMoreProducts);
    const provider = useSelector(getProvider);
    const organization = useSelector(getOrganization);
    const productsSearchEngine = useSelector(getProductsSearchEngine);
    const productDiscounts = useSelector(getProductDiscounts);
    const productDiscountsPending = useSelector(getPendingProductDiscounts);

    const dispatch = useDispatch();
    const limit = 20;
    const [title, setTitle] = useState('');
    const instantSearch = useSettingValue(SettingsKey.INSTANT_SEARCH);
    const instantSearchEnabled = Boolean(
        organization.provider !== 'shopify-private' && instantSearch && provider?.searchEngineId,
    );
    const providerRef = useRef(provider);

    useEffect(() => {
        if (!provider) {
            dispatch(fetchOrganizationProviderAction());
        }
    }, [dispatch, provider]);

    useEffect(() => {
        if (!productsSearchEngine && provider?.searchEngineId) {
            dispatch(fetchProductsSearchEngineAction(provider?.searchEngineId));
        }
    }, [dispatch, provider, productsSearchEngine]);

    useEffect(() => {
        dispatch(fetchProductsDiscountsAction({ queryParams: {} }));
    }, [dispatch]);

    const loadProducts = useCallback(
        (offset) => {
            if (instantSearchEnabled) {
                const queryParams = {
                    page: offset / limit + 1,
                    rpp: limit,
                    text: title,
                    ...(productsSearchEngine?.searchFields?.length
                        ? { searchFields: productsSearchEngine?.searchFields?.join(',') }
                        : {}),
                    ...(productsSearchEngine?.termSearchFields?.length
                        ? { termSearchFields: productsSearchEngine?.termSearchFields?.join(',') }
                        : {}),
                };
                dispatch(fetchProductsV2Action(providerRef.current.searchEngineId, offset, queryParams));
            } else {
                dispatch(
                    fetchProductsAction({
                        type: type,
                        title,
                        offset,
                        limit,
                    }),
                );
            }
        },
        [
            dispatch,
            instantSearchEnabled,
            productsSearchEngine?.searchFields,
            productsSearchEngine?.termSearchFields,
            title,
            type,
        ],
    );

    useEffect(() => {
        providerRef.current = provider;
        if (provider || !instantSearchEnabled) {
            loadProducts(0);
        }
    }, [title, provider, instantSearchEnabled, loadProducts]);

    const handleOnFilterChange = (filter) => {
        setTitle(filter);
    };

    const handleLoadMoreProducts = () => {
        if (products.length) {
            loadProducts(products.length);
        }
    };

    const filter = <EventFormSelectAssetsFilter setSearch={(n) => setTitle(n)} searchMinLength={searchMinLength} />;

    return (
        <EventFormSelectAssets
            searchMinLength={searchMinLength}
            filter={filter}
            search={title}
            sectionName={t('eventForm.products')}
            emptyType={EmptyTypes.EMPTY_PRODUCTS}
            emptyDescription={emptyDescription || t('empty.products')}
            emptyDescriptionNoAssets={t('eventAssets.emptyDescription.products')}
            assets={
                !productDiscountsPending
                    ? products.map((asset) => ({ ...asset, ...getProductDiscount(asset, productDiscounts) }))
                    : products
            }
            error={error}
            pending={pending}
            selectedAssets={
                !productDiscountsPending
                    ? selectedProducts.map((asset) => ({ ...asset, ...getProductDiscount(asset, productDiscounts) }))
                    : selectedProducts
            }
            close={close}
            onConfirm={onConfirm}
            onFilterChange={handleOnFilterChange}
            loadMore={handleLoadMoreProducts}
            hasMore={hasMore}
            idProperty={instantSearchEnabled ? 'variantGroupId' : 'id'}
        />
    );
};

export default EventFormSelectProducts;
